import MojitoCore from 'mojito/core';
import { noop, debounce } from 'mojito/utils';
import { ruleTemplates } from './scroll-pane-style';
import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useEffectOnUpdate } from 'presentation/hooks/index.js';
const log = MojitoCore.logger.get('ScrollPane');
const { DESKTOP } = MojitoCore.Services.SystemSettings.types.APPLICATION_MODE;
const classUtils = MojitoCore.Base.classUtils;
const { useResizeDetector } = MojitoCore.Base.resizeDetector;
const { useCssRule } = MojitoCore.Presentation.Hooks;

const clamp = (val, minVal, maxVal) => Math.max(Math.min(val, maxVal), minVal);

export const onResizeNoop = noop;
export default function ScrollPane(props) {
    const { mojitoTools } = props;
    const { style, config, appContext } = mojitoTools;

    const [scrollState, setScrollState] = useState({});
    const activeScrollingToPosition = useRef();
    const containerElRef = useRef();
    const prevMousePositionRef = useRef();

    const mouseDragScroll =
        config.mouseDragScroll && appContext.systemSettings().applicationMode === DESKTOP;
    const classUUID = useMemo(() => classUtils.createClassUUID('scrollBar-hide'), []);
    const cssVars = useMemo(() => ({ '%class%': classUUID }), [classUUID]);

    useCssRule(classUUID, cssVars, ruleTemplates);

    // For performance reasons, only listen to size changes if there is an
    // explicitly set onResize callback function.
    const shouldListenToSizeChanges = props.onResize !== onResizeNoop || config.dynamicOverflows;

    // Calling getContainerInfo leads to a style recalculations and reflows, that is why it is important to make sure it is not executed unnecessarily or too often
    const getContainerInfo = useCallback(() => {
        const [scrollPropName, clientSizePropName, scrollSizePropName] = {
            x: ['scrollLeft', 'clientWidth', 'scrollWidth'],
            y: ['scrollTop', 'clientHeight', 'scrollHeight'],
        }[config.direction];
        return {
            scrollPosition: containerElRef.current[scrollPropName],
            maxScrollPosition:
                containerElRef.current[scrollSizePropName] -
                containerElRef.current[clientSizePropName],
            viewportSize: containerElRef.current[clientSizePropName],
        };
    }, [config.direction]);

    const onScrollFinished = useMemo(
        () =>
            debounce(() => {
                activeScrollingToPosition.current = undefined;
                props.onScrollFinished();
            }, 100),
        [props]
    );

    const onResize = useMemo(
        () =>
            debounce(() => {
                const containerInfo = getContainerInfo();
                setScrollState({
                    viewportSize: containerInfo.viewportSize,
                    scrollSize: containerInfo.scrollSize,
                    maxScrollPosition: containerInfo.maxScrollPosition,
                });
            }, 300),
        [getContainerInfo]
    );

    const onScroll = useMemo(
        () =>
            debounce(() => {
                onScrollFinished();
                props.onScroll !== noop && props.onScroll(getContainerInfo());
            }, 300),
        [getContainerInfo, props, onScrollFinished]
    );

    const [topBoundMarkerRef, isNaughtScrolled] = useScrolledToEdge(containerElRef.current);
    const [bottomBoundMarkerRef, isFullyScrolled] = useScrolledToEdge(
        containerElRef.current,
        false
    );

    const { elementRef: elementRefCallback } = useResizeDetector({
        onResizeHandler: onResize,
        deps: [onResize],
        ignoreResize: !shouldListenToSizeChanges,
    });

    const onMouseMove = useCallback(
        ev => {
            const prevMousePosition = prevMousePositionRef?.current;
            const { direction } = config;
            const mousePosition = { x: ev.screenX, y: ev.screenY };
            const scrollPropName = { x: 'scrollLeft', y: 'scrollTop' }[direction];
            containerElRef.current[scrollPropName] +=
                prevMousePosition[direction] - mousePosition[direction];
            prevMousePositionRef.current = mousePosition;
        },
        [config]
    );

    const onMouseDown = useCallback(
        ev => {
            if (!containerElRef.current.contains(ev.target)) {
                return;
            }
            prevMousePositionRef.current = { x: ev.screenX, y: ev.screenY };
            window.addEventListener('mousemove', onMouseMove);
        },
        [onMouseMove]
    );

    const onMouseUp = useCallback(
        () => window.removeEventListener('mousemove', onMouseMove),
        [onMouseMove]
    );

    useEffect(() => {
        if (!['x', 'y'].includes(config.direction)) {
            throw new Error('ScrollPane.direction is required');
        }

        if (mouseDragScroll) {
            window.addEventListener('mousedown', onMouseDown);
            window.addEventListener('mouseup', onMouseUp);
        }

        return () => {
            onScrollFinished.cancel();
            onScroll.cancel();
            onResize.cancel();

            if (mouseDragScroll) {
                window.removeEventListener('mousedown', onMouseDown);
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffectOnUpdate(() => {
        const { targetPosition, enableAnimation } = props;

        targetPosition !== undefined && scrollTo(targetPosition, enableAnimation);
    }, [props.targetPosition]);

    useEffectOnUpdate(() => {
        shouldListenToSizeChanges && props.onResize(getContainerInfo());
    }, [scrollState]);

    useEffectOnUpdate(() => {
        log.warn('ScrollPane does not support dynamically switching onScroll callback');
    }, [props.onScroll]);

    const shouldHideScrollBar = () => {
        const { direction, scrollBarYDirectionOff, scrollBarXDirectionOff } = config;

        if (direction === 'x') {
            return scrollBarXDirectionOff;
        }
        return scrollBarYDirectionOff;
    };

    const scrollTo = (targetPosition, animate) => {
        const { maxScrollPosition } = getContainerInfo();
        targetPosition = clamp(targetPosition, 0, maxScrollPosition);
        const scrollPropName = { x: 'scrollLeft', y: 'scrollTop' }[config.direction];
        // if in the middle of scrolling process,
        // prevents unneeded scroll calculation for the same targetPosition scroll action
        if (
            activeScrollingToPosition.current !== undefined &&
            activeScrollingToPosition.current === targetPosition
        ) {
            return;
        }
        activeScrollingToPosition.current = targetPosition;

        if (!animate) {
            containerElRef.current[scrollPropName] = targetPosition;
            return;
        }

        const containerSideName = { x: 'left', y: 'top' }[config.direction];
        containerElRef.current?.scrollTo({
            [containerSideName]: targetPosition,
            behavior: 'smooth',
        });
    };

    const className = classUtils.classNames(
        'ta-ScrollPane',
        props.class,
        shouldHideScrollBar() && classUUID
    );

    const onContainerRefChanged = el => {
        if (containerElRef.current || !el) {
            return;
        }

        elementRefCallback(el);
        containerElRef.current = el;

        if (props.targetPosition !== undefined && config.autoScrollOnMount) {
            scrollTo(props.targetPosition, false);
        }

        if (props.onInit !== noop || config.dynamicOverflows) {
            const containerInfo = getContainerInfo();
            setScrollState({
                viewportSize: containerInfo.viewportSize,
                scrollSize: containerInfo.scrollSize,
                maxScrollPosition: containerInfo.maxScrollPosition,
            });
            props.onInit(containerInfo, el);
        }
    };

    const containerStyle = useMemo(() => {
        const isContentOverflowing = () =>
            Boolean(containerElRef.current) && 0 < scrollState.maxScrollPosition;
        // dynamicOverflows functionality is not essential and only required for better user experience, remove it in case causing any critical issues.
        const paneIsScrollable = config.dynamicOverflows ? isContentOverflowing() : true;
        const containerFullyScrolledStyle = isFullyScrolled
            ? style.containerFullyScrolled
            : undefined;
        const containerNaughtScrolledStyle = isNaughtScrolled
            ? style.containerNaughtScrolled
            : undefined;
        const scrollableContainerStyle =
            containerNaughtScrolledStyle || containerFullyScrolledStyle || style.container;

        const containerStyle = paneIsScrollable
            ? scrollableContainerStyle
            : style.containerNonScrollable;

        return mouseDragScroll && isContentOverflowing()
            ? { ...containerStyle, cursor: 'move' }
            : containerStyle;
    }, [
        isFullyScrolled,
        isNaughtScrolled,
        style,
        scrollState.maxScrollPosition,
        mouseDragScroll,
        config,
    ]);

    return (
        <div
            id={props.id}
            style={containerStyle}
            onScroll={onScroll}
            onTouchMove={props.onTouchMove}
            ref={onContainerRefChanged}
            className={className}
        >
            {config.direction === 'y' && (
                <div className={'ta-topBoundMarker'} ref={topBoundMarkerRef} />
            )}
            {props.children}
            {config.direction === 'y' && (
                <div className={'ta-bottomBoundMarker'} ref={bottomBoundMarkerRef} />
            )}
        </div>
    );
}

ScrollPane.getStyle = (config, mode, merge) => {
    const {
        direction,
        nativeScroll,
        display,
        width = 'auto',
        scrollBarWidth,
        scrollbarGutter,
        styleFullyScrolled,
        styleNaughtScrolled,
        style,
    } = config;
    const scrollBarStyle = nativeScroll ? 'auto' : 'hidden';
    const displaySetting = display ? { display } : {};
    const containerStyle = {
        flexGrow: 1,
        overflowX: direction === 'x' ? scrollBarStyle : 'hidden',
        overflowY: direction === 'y' ? scrollBarStyle : 'hidden',
        scrollbarWidth: scrollBarWidth,
        scrollbarGutter: scrollbarGutter,
        WebkitOverflowScrolling: 'touch',
        ...displaySetting,
        width,
        ...style,
    };
    return {
        container: containerStyle,
        containerNonScrollable: merge(containerStyle, { overflowX: 'hidden', overflowY: 'hidden' }),
        containerFullyScrolled: merge(containerStyle, styleFullyScrolled),
        containerNaughtScrolled: merge(containerStyle, styleNaughtScrolled),
    };
};

const useScrolledToEdge = (rootElement, isOnEdgeDefault = true) => {
    const [scrolledToEdge, setScrolledToEdge] = useState(isOnEdgeDefault);
    const observerRef = useRef();
    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
        };
    }, []);
    const boundMarkerRef = useCallback(
        node => {
            observerRef.current?.disconnect();
            if (node) {
                observerRef.current = new IntersectionObserver(
                    ([entry]) => setScrolledToEdge(entry.isIntersecting),
                    { root: rootElement }
                );
                observerRef.current.observe(node);
            } else {
                observerRef.current = undefined;
            }
        },
        [rootElement]
    );

    return [boundMarkerRef, scrolledToEdge];
};
